/**
 * ObjectCreatedItem - Renders object creation events with cascades.
 *
 * Receives item.events[] where events[0] is the creation event and
 * events[1..N] are cascade events (formula computations on creation, etc.).
 * Combines all property values into a single timeline entry.
 *
 * Display patterns:
 * - Without properties: "**Actor** created this contact"
 * - With properties: "**Actor** created this contact" with expandable sub-items
 */

import { Edit03 as EditIcon } from '@untitledui/icons';
import type { ReactElement, ReactNode } from 'react';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { isAnswersSnapshotProperty } from '@/lib/crm/utils/bookingQuestions';
import { getEntityTypeSentenceLabel } from '@/lib/crm/utils/entityTypeUtils';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { SubjectReference } from '../components/SubjectReference';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import { isStageSeedingCreate } from '../timelineItemVisibility';
import type { PropertyUpdatePayload } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import { buildBookingAnswerSubItems } from '../utils/buildBookingAnswerSubItems';
import {
  buildJsonSubItems,
  getCompanionSchema,
} from '../utils/buildJsonSubItems';
import { getTimelineValueText } from '../utils/valueText';
import type { ItemComponentProps } from './types';

export function ObjectCreatedItem({
  item,
  entityType,
  isContext,
}: ItemComponentProps) {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? Number(orgIdParam) : undefined;
  const { getActorInfo, formatPropertyName, getObjectLabel } =
    useEventFormatters();
  const { lookups } = useViewStateLookups();
  const openSlideout = useOpenSlideoutOptional();

  const handleEntityClick = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  const event = item.events[0];
  const actor = getActorInfo(event);

  // Derived before early return so hooks can safely depend on them.
  const isAssociatedEvent = 'association_type_name' in event;
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;
  const associatedEntityId = associatedObject?.entity_id;

  const handleAssociatedClick = useCallback(() => {
    if (associatedEntityId) {
      handleEntityClick(associatedEntityId);
    }
  }, [associatedEntityId, handleEntityClick]);

  // Hide stage creation events from seeding (system noise, not user-relevant)
  if (isStageSeedingCreate(event)) {
    return null;
  }

  // Get property schema from lookups
  const getPropertySchema = (
    propertyName: string
  ): PropertyTypeSchema | undefined => {
    const key = `${event.entity_type_name}.${propertyName}`;
    return lookups.property_types[key];
  };

  // Get icon for a property using schema lookup
  const getPropertyIcon = (propertyName: string): ReactElement => {
    const iconClass = 'w-4 h-4 text-primary';
    const propertySchema = getPropertySchema(propertyName);

    if (propertySchema) {
      const IconComponent = getPropertyTypeIconComponent(propertySchema);
      return <IconComponent className={iconClass} />;
    }

    return <EditIcon className={iconClass} />;
  };

  // For a de-emphasized context node the event's entity is NOT the record being
  // viewed, so its self-name resolves to the entity's display name (rendered as a
  // badge below) rather than "this contact".
  const contextEntity = isContext
    ? lookups.entities[event.entity_id]
    : undefined;
  const selfDisplayText =
    contextEntity?.ahoy_display_name ??
    `this ${getEntityTypeSentenceLabel(entityType)}`;
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : selfDisplayText;

  const isNoteObject = associatedObject?.entity_type_name === 'ahoy_note';
  let objectDisplay: ReactNode;
  if (isAssociatedEvent && associatedObject && !isNoteObject) {
    objectDisplay = (
      <EntityBadge
        state={{
          kind: 'data',
          data: {
            id: associatedObject.entity_id,
            entityType: associatedObject.entity_type_name,
            displayName: associatedObject.ahoy_display_name ?? 'Unknown',
          },
        }}
        className='inline-flex align-middle'
        onClick={openSlideout ? handleAssociatedClick : undefined}
      />
    );
  } else if (isAssociatedEvent) {
    objectDisplay = bold(objectDisplayText);
  } else {
    // Primary node → "this contact"; context node → badge the subject entity.
    objectDisplay = (
      <SubjectReference
        isContext={isContext}
        entityId={event.entity_id}
        fallback={objectDisplayText}
        onEntityClick={openSlideout ? handleEntityClick : undefined}
      />
    );
  }

  // Combine properties from ALL events (creation event + cascades)
  const allProperties: Record<string, unknown> = {};

  for (const ev of item.events) {
    const payload = ev.payload as PropertyUpdatePayload | undefined;
    const props = payload?.properties || {};
    Object.assign(allProperties, props);
  }

  // Filter to only show properties with value_read_access: 'all'
  const visibleProperties = Object.entries(allProperties).filter(
    ([propName]) => {
      const schema = getPropertySchema(propName);
      return schema?.value_read_access === 'all';
    }
  );

  const isChromeExtension =
    event.source_type_name === 'ahoy_chrome_extension' ||
    event.source_type_name === 'ahoy_linkedin_extension';

  const ariaLabel = isChromeExtension
    ? `${actor.name} created this ${getEntityTypeSentenceLabel(entityType)} via the Ahoy Chrome Extension`
    : `${actor.name} created ${objectDisplayText}`;

  const headerContent = isChromeExtension ? (
    <InlineText>
      <ActorName actor={actor} /> created this{' '}
      {getEntityTypeSentenceLabel(entityType)} via the{' '}
      {bold('Ahoy Chrome Extension')}
    </InlineText>
  ) : (
    <InlineText>
      <ActorName actor={actor} /> created {objectDisplay}
    </InlineText>
  );

  // Audit events: show ahoy_summary as rendered markdown (no icon/label chrome)
  if (event.entity_type_name === 'ahoy_audit') {
    const summaryValue = allProperties.ahoy_summary;
    const summaryText =
      typeof summaryValue === 'string' ? summaryValue : undefined;

    return (
      <TimelineEvent ariaLabel={ariaLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Content
          header={headerContent}
          timestamp={event.occurred_at}
        >
          {summaryText && (
            <MarkdownContent
              content={summaryText}
              size='compact'
              orgId={orgId}
              className='mt-4 text-[var(--color-text-secondary)] [&_.markdown-alert>:first-child]:hidden'
            />
          )}
        </TimelineEvent.Content>
      </TimelineEvent>
    );
  }

  // If no visible properties, show simple message
  if (visibleProperties.length === 0) {
    return (
      <TimelineEvent ariaLabel={ariaLabel}>
        <TimelineEvent.Icon event={event} />
        <TimelineEvent.Header timestamp={event.occurred_at}>
          {headerContent}
        </TimelineEvent.Header>
      </TimelineEvent>
    );
  }

  // Generate sub-items for each property.
  // JSON properties with a companion schema are decomposed into per-field sub-items.
  const subItems: SubItemContent[] = visibleProperties.flatMap(
    ([propertyName, value]) => {
      const schema = getPropertySchema(propertyName);

      // JSON decomposition
      const companionSchema = getCompanionSchema(
        schema,
        event.internal_payload
      );
      if (
        companionSchema &&
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return buildJsonSubItems(
          value as Record<string, unknown>,
          companionSchema,
          lookups,
          openSlideout ? handleEntityClick : undefined
        );
      }

      // Answers-snapshot decomposition: one sub-item per booking question,
      // peers of the other fields (see buildBookingAnswerSubItems).
      if (isAnswersSnapshotProperty(propertyName)) {
        return buildBookingAnswerSubItems({ value, orgId });
      }

      const displayName = formatPropertyName(
        propertyName,
        event.entity_type_name
      );
      const icon = getPropertyIcon(propertyName);

      return {
        icon,
        text: (
          <span>
            {displayName}:{' '}
            <PropertyValueLabel
              value={value}
              orgId={orgId}
              propertyType={schema}
              fontSize='sm'
              className='font-medium'
              maxLength={150}
              entityProperties={allProperties}
            />
          </span>
        ),
        ariaLabel: `${displayName}: ${getTimelineValueText(value, { empty: '—' })}`,
      };
    }
  );

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        header={headerContent}
        timestamp={event.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
